import dayjs from 'dayjs';

import { DATE_ID_FORMAT } from '@/helpers';
import { logger } from '@/utils/logger/logger';

import { PLANNER_PAGINATION_LOG_TAG } from '../../constants';

const TEAM_MEMBER_OFFSET_PX = 191.5;
const SCROLL_ANIMATION_DURATION_MS = 150;
const ELEMENT_WAIT_MAX_ATTEMPTS = 15;
const ELEMENT_WAIT_DELAY_MS = 20;

let currentAnimationId: number | null = null;
let isAnimating = false;
let animationStartScrollLeft = 0;
let animationTargetScrollLeft = 0;

/**
 * Check if a scroll animation is currently in progress
 */
export const isScrollAnimating = (): boolean => {
  return isAnimating;
};

/**
 * Get the target scroll position of the current animation
 */
export const getAnimationTarget = (): number | null => {
  return isAnimating ? animationTargetScrollLeft : null;
};

/**
 * Get the start scroll position of the current animation
 */
export const getAnimationStart = (): number => {
  return animationStartScrollLeft;
};

/**
 * Easing function for smooth deceleration (ease-in-out-cubic)
 * Creates a smooth acceleration and deceleration effect
 */
const easeInOutCubic = (t: number): number => {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
};

/**
 * Smoothly animates scroll position with ease-in-out interpolation
 * Cancels any ongoing animation before starting a new one
 * Sets flags to prevent expensive updates during animation
 */
const animateScroll = (
  container: HTMLElement,
  targetScrollLeft: number,
  duration: number = SCROLL_ANIMATION_DURATION_MS
) => {
  if (currentAnimationId !== null) {
    cancelAnimationFrame(currentAnimationId);
    currentAnimationId = null;
  }

  const startScrollLeft = container.scrollLeft;
  const distance = targetScrollLeft - startScrollLeft;

  if (Math.abs(distance) < 1) {
    return;
  }

  container.dataset.scrollAnimating = 'true';
  isAnimating = true;
  animationStartScrollLeft = startScrollLeft;
  animationTargetScrollLeft = targetScrollLeft;

  logger.debug(
    PLANNER_PAGINATION_LOG_TAG,
    'animateScroll: START from=' +
      Math.round(startScrollLeft) +
      ' to=' +
      Math.round(targetScrollLeft) +
      ' distance=' +
      Math.round(distance)
  );

  const startTime = performance.now();

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeInOutCubic(progress);

    container.scrollLeft = startScrollLeft + distance * easedProgress;

    if (progress < 1) {
      currentAnimationId = requestAnimationFrame(animate);
    } else {
      currentAnimationId = null;
      isAnimating = false;
      delete container.dataset.scrollAnimating;

      logger.debug(
        PLANNER_PAGINATION_LOG_TAG,
        'animateScroll: COMPLETE, dispatching scrollAnimationComplete'
      );
      // Dispatch custom event immediately to force updates
      container.dispatchEvent(new Event('scrollAnimationComplete'));
    }
  };

  currentAnimationId = requestAnimationFrame(animate);
};

/**
 * Waits for an element to be available in the DOM with retry logic
 */
const waitForElement = (
  elementId: string,
  maxAttempts: number = ELEMENT_WAIT_MAX_ATTEMPTS,
  delayMs: number = ELEMENT_WAIT_DELAY_MS
): Promise<HTMLElement | null> => {
  return new Promise(resolve => {
    let attempts = 0;

    const check = () => {
      const element = document.getElementById(elementId);

      if (element) {
        resolve(element);
        return;
      }

      attempts++;
      if (attempts >= maxAttempts) {
        resolve(null);
        return;
      }

      setTimeout(check, delayMs);
    };

    check();
  });
};

/**
 * Scrolls to the specified date, centering it in the viewport
 */
export const scrollToDate = async (date: dayjs.Dayjs) => {
  const dateStr = date.format(DATE_ID_FORMAT);
  const container = document.getElementById('planner-container');

  if (!container) return;

  const headerCell = await waitForElement(`header-cell-${dateStr}`);

  if (headerCell) {
    const cellPosition = headerCell.offsetLeft;
    const containerWidth = container.clientWidth;
    const targetScrollLeft = cellPosition - containerWidth / 2;
    animateScroll(container, targetScrollLeft);
  }
};

/**
 * Instantly positions the viewport centered on the specified date, without animation.
 * Used for initial load to avoid a distracting scroll animation after the skeleton screen.
 */
export const scrollToDateInstant = async (date: dayjs.Dayjs) => {
  const dateStr = date.format(DATE_ID_FORMAT);
  const container = document.getElementById('planner-container');

  if (!container) return;

  const headerCell = await waitForElement(`header-cell-${dateStr}`);

  if (headerCell) {
    const cellPosition = headerCell.offsetLeft;
    const containerWidth = container.clientWidth;
    container.scrollLeft = cellPosition - containerWidth / 2;
    container.dispatchEvent(new Event('scrollAnimationComplete'));
  }
};

/** Beyond this many viewport-widths of distance, smart scrolling jumps instantly instead of animating */
const SMART_SCROLL_INSTANT_THRESHOLD_VIEWPORTS = 3;

/**
 * Returns the scrollLeft that centers the given date in the viewport,
 * or null when the date's header cell is not currently in the DOM
 * (date outside the loaded dates window). Synchronous — no retries.
 */
export const getCenteredScrollLeftForDate = (date: dayjs.Dayjs): number | null => {
  const container = document.getElementById('planner-container');
  const headerCell = document.getElementById(`header-cell-${date.format(DATE_ID_FORMAT)}`);
  if (!container || !headerCell) return null;

  return Math.max(0, headerCell.offsetLeft - container.clientWidth / 2);
};

/**
 * Centers the given date in the viewport: animates when the target is nearby,
 * jumps instantly when it is more than a few viewport-widths away (a long
 * animation over hundreds of days reads as a disorienting blur).
 */
export const scrollToDateSmart = async (date: dayjs.Dayjs) => {
  const dateStr = date.format(DATE_ID_FORMAT);
  const container = document.getElementById('planner-container');

  if (!container) return;

  const headerCell = await waitForElement(`header-cell-${dateStr}`);
  if (!headerCell) return;

  const targetScrollLeft = Math.max(0, headerCell.offsetLeft - container.clientWidth / 2);
  const distance = Math.abs(targetScrollLeft - container.scrollLeft);

  if (distance > SMART_SCROLL_INSTANT_THRESHOLD_VIEWPORTS * container.clientWidth) {
    container.scrollLeft = targetScrollLeft;
    container.dispatchEvent(new Event('scrollAnimationComplete'));
  } else {
    animateScroll(container, targetScrollLeft);
  }
};

/**
 * Scrolls to the specified date, positioning it at the left edge of the viewport
 */
export const scrollToDateStart = async (date: dayjs.Dayjs) => {
  const dateStr = date.format(DATE_ID_FORMAT);
  const container = document.getElementById('planner-container');

  if (!container) return;

  const headerCell = await waitForElement(`header-cell-${dateStr}`);

  if (headerCell) {
    const targetScrollLeft = headerCell.offsetLeft - TEAM_MEMBER_OFFSET_PX;
    animateScroll(container, targetScrollLeft);
  }
};
